// Status enum espelhado de bi.fact_onu_status_5s.status (techdash/db/001_bi_map_schema.sql).
// Mantém paridade com apps/poller/oid.go statusFromHuawei().

export const STATUS = {
  OFFLINE: 0,
  ONLINE: 1,
  LOS: 2,
  DYING_GASP: 3,
  POWER_OFF: 4,
  CONFIG_FAILURE: 5,
  UNKNOWN: 9,
};

export const STATUS_LABEL = {
  0: 'offline',
  1: 'online',
  2: 'LOS',
  3: 'dying gasp',
  4: 'power off',
  5: 'config failure',
  9: 'unknown',
};

// Cores HEX por status — paleta NOC (alta saturação para alarme).
export const STATUS_COLOR = {
  0: '#dc2626', // offline — vermelho (NOC: offline = cliente sem sinal = problema)
  1: '#10b981', // online — verde
  2: '#ef4444', // LOS — vermelho forte
  3: '#f59e0b', // dying gasp — laranja
  4: '#7c3aed', // power off — roxo
  5: '#ec4899', // config failure — pink
  9: '#6b7280', // unknown — cinza médio
};

// "Bad" = não-normal (mostrar com destaque no mapa).
// Inclui OFFLINE — desde 2026-05-12, alinhado com IsBad do poller Go.
export const BAD_STATUSES = [
  STATUS.OFFLINE,
  STATUS.LOS,
  STATUS.DYING_GASP,
  STATUS.POWER_OFF,
  STATUS.CONFIG_FAILURE,
];

export const isBad = (status) => BAD_STATUSES.includes(status);
export const isLos = (status) => status === STATUS.LOS;

// Tempo legível desde X (ex: "5min", "2h", "3d").
export function timeAgo(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return '—';
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'agora';
  if (min < 60) return `${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h${min % 60 ? ` ${min % 60}min` : ''}`;
  const d = Math.floor(h / 24);
  return `${d}d${h % 24 ? ` ${h % 24}h` : ''}`;
}

// effectiveSince — escolhe o melhor timestamp para "desde quando":
// prefere o ground truth da OLT que sobrevive a restart do poller; fallback
// status_since (poller); observed_at.
// API v2 retorna `last_change_at_olt` no path Postgres canônico (mart) e
// `last_change_at` no path Redis (snapshot direto do poller Go) — aceitamos
// ambos por compat até o backend normalizar via merge no _enrich.
export function effectiveSince(onu) {
  if (!onu) return null;
  return (
    onu.last_change_at_olt ||
    onu.last_change_at ||
    onu.status_since ||
    onu.observed_at ||
    null
  );
}

// Buckets de envelhecimento (aging) — quanto tempo no estado atual.
// Útil para NOC: ONUs caídas há muito tempo são mais críticas que recém-caídas
// (ou ao contrário, dependendo da operação). Buckets espelham as faixas
// pedidas pelo NOC: <30min, 30min-1h, 1-3h, 3-6h, 6-12h, >12h.
export const AGING_BUCKETS = [
  { key: 'lt30m',   label: '<30min', maxMin: 30,   color: '#fbbf24' }, // amarelo
  { key: '30m-1h',  label: '30m-1h', maxMin: 60,   color: '#f97316' }, // laranja
  { key: '1-3h',    label: '1-3h',   maxMin: 180,  color: '#ef4444' }, // vermelho
  { key: '3-6h',    label: '3-6h',   maxMin: 360,  color: '#dc2626' }, // vermelho escuro
  { key: '6-12h',   label: '6-12h',  maxMin: 720,  color: '#b91c1c' }, // bordô
  { key: 'gt12h',   label: '>12h',   maxMin: Infinity, color: '#7c2d12' }, // marrom
];

export function agingBucket(onu) {
  if (!onu) return null;
  // Não aplicável para ONUs online — usuário pediu para LOS/bad.
  if (onu.status === 1) return null;
  const since = effectiveSince(onu);
  if (!since) return null;
  const min = (Date.now() - new Date(since).getTime()) / 60000;
  if (min < 0) return null;
  for (const b of AGING_BUCKETS) {
    if (min < b.maxMin) return b;
  }
  return AGING_BUCKETS[AGING_BUCKETS.length - 1];
}

export const AGING_COLOR_BY_KEY = AGING_BUCKETS.reduce((acc, b) => {
  acc[b.key] = b.color;
  return acc;
}, {});
